export type HighlightSegment = {
  text: string;
  bold: boolean;
};

// Numbers/percentages — the quantitative facts. Matched without \b so a trailing
// "%" is kept.
const NUMBER_PATTERN = '\\d[\\d,]*(?:\\.\\d+)?%?';

// Static domain phrases a manager scans for. Clinical roles are deliberately NOT
// listed here: they vary by service line, so the caller passes the run's
// configured role abbreviations (loaded from the backend, the same `abbreviation`
// the digest feeds the model) and we merge them in — see `highlightSegments`.
// Multi-word phrases stay ahead of their sub-strings so the longest match wins.
const KEYWORD_PATTERN = [
  'fill rate',
  'charge nurses?',
  'charge shifts?',
  'hard violations?',
  'shift[- ]pattern(?:[- ]preference)?',
  'consecutive[- ]weekends?',
].join('|');

// Escape regex metacharacters in backend-supplied role abbreviations before they
// enter the alternation (an abbreviation is operator-configured free text).
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Word-bounded number + keyword + role alternation, wrapped in ONE capturing
// group so `String.split` interleaves the matched (bold) runs with the plain
// text between them: [plain, match, plain, match, ...]. Roles are escaped and
// ordered longest-first; an empty/blank list is dropped so it never produces an
// empty (always-matching) alternation branch.
function buildHighlightRegex(roles: string[]): RegExp {
  const rolePattern = roles
    .filter((role) => role.trim() !== '')
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  const keywords = [KEYWORD_PATTERN, rolePattern].filter(Boolean).join('|');

  return new RegExp(`(${NUMBER_PATTERN}|\\b(?:${keywords})\\b)`, 'gi');
}

/**
 * Splits AI-summary text into ordered segments, flagging the keyword/number/role
 * runs that should render bold. `roles` is the run's configured clinical role
 * abbreviations (RN, UAP, Unit Secretary, …) loaded from the backend, merged into
 * the highlight set so the bolded roles always track the service line's actual
 * config rather than a hardcoded guess. Returns the original text verbatim across
 * all segments (concatenating them reproduces the input), so the renderer only
 * decides emphasis — never content.
 */
export function highlightSegments(
  text: string,
  roles: string[] = []
): HighlightSegment[] {
  return text
    .split(buildHighlightRegex(roles))
    .map((part, index) => ({ text: part, bold: index % 2 === 1 }))
    .filter((segment) => segment.text !== '');
}
